import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert
} from "@mui/material";

const EnterpriseMail = () => {
  const [empresa, setEmpresa] = useState({
    id: null,
    smtp_host: "",
    smtp_port: "",
    smtp_user: "",
    smtp_password: "",
    smtp_from_name: "",
    smtp_security: "SSL",
    smtp_use_custom_config: false
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: ""
  });

  const getToken = () => {
    return (
      localStorage.getItem("access") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("token") ||
      ""
    );
  };

  const getHeaders = () => {
    const token = getToken();

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    const loadEmpresa = async () => {
      try {
        const res = await fetch("/enterprise/empresa/obtener/", {
          method: "GET",
          headers: getHeaders()
        });

        const data = await res.json();

        if (res.ok && data?.empresa) {
          const e = data.empresa;
          setEmpresa({
            id: e.id,
            smtp_host: e.smtp_host || "",
            smtp_port: e.smtp_port || "",
            smtp_user: e.smtp_user || "",
            smtp_password: e.smtp_password || "",
            smtp_from_name: e.smtp_from_name || "",
            smtp_security: e.smtp_security || "SSL",
            smtp_use_custom_config: !!e.smtp_use_custom_config
          });
        } else {
          setSnackbar({
            open: true,
            severity: "error",
            message: data?.error || "No se pudo cargar la empresa."
          });
        }
      } catch {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Error de conexión con el servidor"
        });
      }
    };

    loadEmpresa();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmpresa((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitch = (e) => {
    const { name, checked } = e.target;
    setEmpresa((prev) => ({ ...prev, [name]: checked }));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!empresa.id) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Primero debe registrar la empresa."
      });
      return;
    }

    try {
      const res = await fetch(`/enterprise/empresa/editar/${empresa.id}/`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          smtp_host: empresa.smtp_host,
          smtp_port: empresa.smtp_port ? Number(empresa.smtp_port) : null,
          smtp_user: empresa.smtp_user,
          smtp_password: empresa.smtp_password,
          smtp_from_name: empresa.smtp_from_name,
          smtp_security: empresa.smtp_security,
          smtp_use_custom_config: empresa.smtp_use_custom_config
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Configuración SMTP guardada correctamente"
        });
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            typeof data?.error === "string"
              ? data.error
              : Object.values(data?.error || data || {})
                  .flat()
                  .join(" ") || "No se pudo guardar la configuración SMTP."
        });
      }
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Error de conexión con el servidor"
      });
    }
  };

  return (
    <Paper
      sx={{
        p: 4,
        borderRadius: "12px",
        boxShadow: "0 6px 18px rgba(0,0,0,0.1)"
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          color: "#1565C0",
          mb: 3
        }}
      >
        Configuración de Correo Empresarial
      </Typography>

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={empresa.smtp_use_custom_config}
                  onChange={handleSwitch}
                  name="smtp_use_custom_config"
                />
              }
              label="Usar servidor SMTP personalizado"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Servidor SMTP"
              name="smtp_host"
              fullWidth
              value={empresa.smtp_host}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Puerto SMTP"
              name="smtp_port"
              type="number"
              fullWidth
              value={empresa.smtp_port}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Correo remitente"
              name="smtp_user"
              fullWidth
              value={empresa.smtp_user}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre del remitente"
              name="smtp_from_name"
              fullWidth
              value={empresa.smtp_from_name}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Contraseña SMTP"
              name="smtp_password"
              type="password"
              fullWidth
              value={empresa.smtp_password}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Seguridad SMTP"
              name="smtp_security"
              fullWidth
              value={empresa.smtp_security}
              onChange={handleChange}
            >
              <MenuItem value="SSL">SSL</MenuItem>
              <MenuItem value="TLS">TLS</MenuItem>
              <MenuItem value="NONE">NINGUNO</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                backgroundColor: "#1565C0",
                "&:hover": { backgroundColor: "#0D47A1" },
                textTransform: "none",
                fontWeight: 600
              }}
            >
              Guardar configuración
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EnterpriseMail;